import { Award, Dumbbell, Flame, Sparkles, Trophy } from "lucide-react";
import { useAdventure, useSummary } from "../lib/api";
import { fmtXp } from "../lib/xp";
import { Empty, fmtDate, fmtTime } from "../components/ui";
import type { AdventureEntry } from "../lib/types";

const icon: Record<AdventureEntry["kind"], typeof Award> = {
  level_up: Trophy,
  skill_unlocked: Sparkles,
  quest_completed: Award,
  session: Dumbbell,
  streak: Flame,
};

export function Journal() {
  const log = useAdventure();
  const summary = useSummary(14);
  const byDate = new Map<string, AdventureEntry[]>();
  for (const e of log.data ?? [])
    byDate.set(e.local_date, [...(byDate.get(e.local_date) ?? []), e]);
  const days = [...byDate.keys()];

  return (
    <div className="grid gap-4">
      <h1 className="text-3xl font-semibold tracking-tight">Journal</h1>

      <section className="card p-4">
        <p className="mb-2 text-xs uppercase tracking-widest text-ink-mute">Last 14 days</p>
        <div className="flex h-16 items-end gap-1">
          {[...(summary.data ?? [])].reverse().map((d) => {
            const max = Math.max(1, ...(summary.data ?? []).map((x) => x.xp_total));
            return (
              <div
                key={d.local_date}
                className="flex flex-1 flex-col items-center gap-1"
                title={`${fmtDate(d.local_date)}: ${fmtXp(d.xp_total)} xp`}
              >
                <div
                  className="w-full rounded-sm bg-accent/70"
                  style={{ height: `${Math.max(4, (d.xp_total / max) * 56)}px` }}
                />
              </div>
            );
          })}
          {(summary.data?.length ?? 0) === 0 && <Empty>No days logged yet.</Empty>}
        </div>
      </section>

      {log.isLoading && <Empty>Loading…</Empty>}
      {days.length === 0 && !log.isLoading && (
        <Empty>Your adventure log is empty. Go and do something.</Empty>
      )}
      {days.map((d) => (
        <section key={d} className="grid gap-2">
          <h2 className="text-sm font-medium text-ink-mute">{fmtDate(d)}</h2>
          <ul className="card divide-y divide-border">
            {byDate.get(d)!.map((e) => {
              const Icon = icon[e.kind];
              const tone =
                e.kind === "level_up" || e.kind === "skill_unlocked"
                  ? "text-accent"
                  : e.kind === "streak"
                    ? "text-warn"
                    : "text-ink-soft";
              return (
                <li key={e.id} className="flex items-center gap-3 px-4 py-3">
                  <Icon size={18} className={tone} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm">{e.title}</p>
                  </div>
                  <span className="text-xs text-ink-mute">{fmtTime(e.at)}</span>
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
}
